import React, { useState, useEffect } from 'react';
import { 
  RotateCcw, Package, RefreshCw, DollarSign, FileText, 
  AlertTriangle, CheckCircle, Clock, History
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Return/Replacement Types
const RR_TYPES = [
  { value: 'return_replace', label: 'Return & Replace', icon: RefreshCw, description: 'Customer returns item, receives replacement' },
  { value: 'return_only', label: 'Return Only', icon: RotateCcw, description: 'Customer returns item, no replacement' },
  { value: 'replace_only', label: 'Replace Only', icon: Package, description: 'Send replacement without return' },
  { value: 'return_refund', label: 'Return & Refund', icon: DollarSign, description: 'Customer returns item, receives refund' },
  { value: 'refund_only', label: 'Refund Only', icon: DollarSign, description: 'Issue refund without return' },
];

// Replacement Reasons
const REPLACEMENT_REASONS = [
  { value: 'damaged', label: 'Damaged' },
  { value: 'manufacturing_defect', label: 'Manufacturing Defect' },
  { value: 'wrong_item', label: 'Wrong Item Sent' },
  { value: 'customer_preference', label: 'Customer Preference' },
];

// Reopen Order Dialog
export const ReopenOrderDialog = ({ open, onClose, order, onSuccess }) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleReopen = async () => {
    if (!reason || reason.trim().length < 5) {
      toast.error('Please provide a reason (minimum 5 characters)');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/orders/${order.id}/reopen`,
        { reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Order reopened successfully');
      onSuccess(response.data.order);
      onClose();
      setReason('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reopen order');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-wt-paper border-wt-border max-w-md">
        <DialogHeader>
          <DialogTitle className="text-wt-text-primary flex items-center gap-2">
            <RotateCcw className="w-5 h-5 text-wt-gold" />
            Reopen Order #{order?.order_number}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5" />
              <div className="text-sm text-yellow-300">
                <p className="font-medium">This will:</p>
                <ul className="list-disc list-inside mt-1 text-yellow-300/80">
                  <li>Change status from Delivered to Reopened</li>
                  <li>Clear driver assignment for re-delivery</li>
                  <li>Log this action in the audit trail</li>
                </ul>
              </div>
            </div>
          </div>

          <div>
            <Label className="text-wt-text-primary">Reason for Reopening *</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason for reopening this order..."
              className="bg-wt-bg border-wt-border text-wt-text-primary mt-2"
              rows={3}
            />
            <p className="text-xs text-wt-text-muted mt-1">Minimum 5 characters required</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-wt-border text-wt-text-primary">
            Cancel
          </Button>
          <Button
            onClick={handleReopen}
            disabled={loading || !reason || reason.trim().length < 5}
            className="bg-wt-gold text-black hover:bg-wt-gold-hover"
          >
            {loading ? 'Reopening...' : 'Reopen Order'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Return/Replacement Dialog
export const ReturnReplacementDialog = ({ open, onClose, order, inventory = [], onSuccess }) => {
  const [rrType, setRrType] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [replacementItems, setReplacementItems] = useState([]);
  const [returnSel, setReturnSel] = useState({}); // sku -> { selected, qty }
  const [loading, setLoading] = useState(false);

  const isReplacementType = rrType === 'replace_only' || rrType === 'return_replace';
  const isReturnType = ['return_replace', 'return_only', 'return_refund'].includes(rrType);

  useEffect(() => {
    if (open && order) {
      const init = {};
      (order.items || []).forEach((it) => {
        if (it.sku) init[it.sku] = { selected: true, qty: it.quantity || 1, max: it.quantity || 1 };
      });
      setReturnSel(init);
    }
  }, [open, order]);

  const handleAddReplacementItem = () => {
    setReplacementItems([
      ...replacementItems,
      { original_sku: '', new_sku: '', name: '', quantity: 1, price: 0, reason: reason }
    ]);
  };

  const handleUpdateReplacementItem = (index, field, value) => {
    const updated = [...replacementItems];
    updated[index][field] = value;
    setReplacementItems(updated);
  };

  const handleRemoveReplacementItem = (index) => {
    setReplacementItems(replacementItems.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!rrType) {
      toast.error('Please select a type');
      return;
    }
    if (!reason) {
      toast.error('Please select a reason');
      return;
    }
    if (isReplacementType && replacementItems.length === 0) {
      toast.error('Please add at least one replacement item');
      return;
    }
    const selectedReturnItems = isReturnType
      ? Object.entries(returnSel)
          .filter(([, v]) => v.selected && v.qty > 0)
          .map(([sku, v]) => ({ sku, quantity: Math.min(parseInt(v.qty, 10) || 1, v.max) }))
      : [];
    if (isReturnType && selectedReturnItems.length === 0) {
      toast.error('Select at least one item to return');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/orders/${order.id}/return-replacement`,
        {
          type: rrType,
          reason,
          notes,
          return_items: selectedReturnItems,
          replacement_items: isReplacementType ? replacementItems : []
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Show success message
      toast.success(response.data.message);
      if (response.data.return_record?.return_number) {
        toast.info(`Return ${response.data.return_record.return_number} created — track it on the Returns page`);
      }
      if (response.data.replacement_order?.order_number) {
        toast.info(`Replacement order ${response.data.replacement_order.order_number} created`);
      }
      
      // Show Shopify sync result if available
      const shopifyResult = response.data.shopify_result;
      if (shopifyResult) {
        if (shopifyResult.action === 'return_created' && shopifyResult.return?.success) {
          toast.success('Return created in Shopify successfully');
        } else if (shopifyResult.action === 'return_created' && !shopifyResult.return?.success) {
          toast.warning(`Shopify return: ${shopifyResult.return?.message || 'Partial success - check Shopify'}`);
        } else if (shopifyResult.action === 'timeline_note_added') {
          toast.info('Timeline note added to Shopify order');
        } else if (shopifyResult.action === 'failed') {
          toast.error(`Shopify sync failed: ${shopifyResult.error}`);
        }
      }
      
      onSuccess(response.data.order);
      onClose();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process request');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setRrType('');
    setReason('');
    setNotes('');
    setReplacementItems([]);
  };

  return (
    <Dialog open={open} onOpenChange={() => { onClose(); resetForm(); }}>
      <DialogContent className="bg-wt-paper border-wt-border max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-wt-text-primary flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-wt-gold" />
            Return / Replacement - Order #{order?.order_number}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Type Selection */}
          <div>
            <Label className="text-wt-text-primary mb-3 block">Select Type *</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {RR_TYPES.map((type) => (
                <button
                  key={type.value}
                  onClick={() => setRrType(type.value)}
                  className={`p-3 rounded-lg border text-left transition-all ${
                    rrType === type.value
                      ? 'border-wt-gold bg-wt-gold/10'
                      : 'border-wt-border hover:border-wt-gold/50 bg-wt-bg'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <type.icon className={`w-4 h-4 ${rrType === type.value ? 'text-wt-gold' : 'text-wt-text-muted'}`} />
                    <span className={`font-medium ${rrType === type.value ? 'text-wt-gold' : 'text-wt-text-primary'}`}>
                      {type.label}
                    </span>
                  </div>
                  <p className="text-xs text-wt-text-muted">{type.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Reason Selection */}
          <div>
            <Label className="text-wt-text-primary">Reason *</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary mt-2">
                <SelectValue placeholder="Select reason" />
              </SelectTrigger>
              <SelectContent className="bg-wt-paper border-wt-border">
                {REPLACEMENT_REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value} className="text-wt-text-primary">
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Items to return (partial selection) */}
          {isReturnType && (
            <div>
              <Label className="text-wt-text-primary mb-2 block">Items to Return *</Label>
              <div className="space-y-2" data-testid="return-items-selection">
                {(order?.items || []).filter((it) => it.sku).map((it) => {
                  const sel = returnSel[it.sku] || { selected: false, qty: 1, max: it.quantity || 1 };
                  return (
                    <div key={it.sku} className={`flex items-center gap-3 p-2 rounded-lg border ${sel.selected ? 'border-wt-gold/50 bg-wt-gold/5' : 'border-wt-border bg-wt-bg'}`}>
                      <input
                        type="checkbox"
                        checked={sel.selected}
                        onChange={(e) => setReturnSel({ ...returnSel, [it.sku]: { ...sel, selected: e.target.checked } })}
                        className="w-4 h-4 accent-amber-500"
                        data-testid={`return-item-check-${it.sku}`}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-wt-text-primary truncate">{it.name || it.title}</p>
                        <p className="text-xs text-wt-text-muted">{it.sku} · ordered {it.quantity || 1}</p>
                      </div>
                      <input
                        type="number"
                        min="1"
                        max={sel.max}
                        value={sel.qty}
                        disabled={!sel.selected}
                        onChange={(e) => setReturnSel({ ...returnSel, [it.sku]: { ...sel, qty: e.target.value } })}
                        className="w-16 p-1.5 rounded bg-wt-bg border border-wt-border text-wt-text-primary text-sm disabled:opacity-40"
                        data-testid={`return-item-qty-${it.sku}`}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Replacement Items (for replace_only or return_replace) */}
          {isReplacementType && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-wt-text-primary">Replacement Items *</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddReplacementItem}
                  className="border-wt-gold text-wt-gold hover:bg-wt-gold/10"
                >
                  + Add Item
                </Button>
              </div>

              {/* Original Items Reference */}
              <div className="p-3 rounded-lg bg-wt-bg border border-wt-border mb-4">
                <p className="text-xs text-wt-text-muted mb-2">Original Items:</p>
                {order?.items?.map((item, idx) => (
                  <div key={idx} className="text-sm text-wt-text-secondary">
                    {item.sku || 'N/A'} - {item.name} (Qty: {item.quantity})
                  </div>
                ))}
              </div>

              {replacementItems.length === 0 ? (
                <p className="text-sm text-wt-text-muted text-center py-4">
                  Click "Add Item" to add replacement products
                </p>
              ) : (
                <div className="space-y-4">
                  {replacementItems.map((item, index) => (
                    <div key={index} className="p-4 rounded-lg border border-wt-border bg-wt-bg">
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-sm font-medium text-wt-text-primary">Item {index + 1}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveReplacementItem(index)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          Remove
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-wt-text-muted">Original SKU</Label>
                          <Input
                            value={item.original_sku}
                            onChange={(e) => handleUpdateReplacementItem(index, 'original_sku', e.target.value)}
                            placeholder="Original SKU"
                            className="bg-wt-paper border-wt-border text-wt-text-primary mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-wt-text-muted">New SKU</Label>
                          <Input
                            value={item.new_sku}
                            onChange={(e) => handleUpdateReplacementItem(index, 'new_sku', e.target.value)}
                            placeholder="New SKU"
                            className="bg-wt-paper border-wt-border text-wt-text-primary mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-wt-text-muted">Product Name</Label>
                          <Input
                            value={item.name}
                            onChange={(e) => handleUpdateReplacementItem(index, 'name', e.target.value)}
                            placeholder="Product name"
                            className="bg-wt-paper border-wt-border text-wt-text-primary mt-1"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs text-wt-text-muted">Quantity</Label>
                            <Input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => handleUpdateReplacementItem(index, 'quantity', parseInt(e.target.value) || 1)}
                              className="bg-wt-paper border-wt-border text-wt-text-primary mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-wt-text-muted">Price</Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.price}
                              onChange={(e) => handleUpdateReplacementItem(index, 'price', parseFloat(e.target.value) || 0)}
                              className="bg-wt-paper border-wt-border text-wt-text-primary mt-1"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Notes */}
          <div>
            <Label className="text-wt-text-primary">Additional Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional notes..."
              className="bg-wt-bg border-wt-border text-wt-text-primary mt-2"
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { onClose(); resetForm(); }} className="border-wt-border text-wt-text-primary">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || !rrType || !reason}
            className="bg-wt-gold text-black hover:bg-wt-gold-hover"
          >
            {loading ? 'Processing...' : 'Submit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Refund Dialog
export const RefundDialog = ({ open, onClose, order, formatCurrency, onSuccess }) => {
  const [refundStatus, setRefundStatus] = useState(order?.refund_status || 'not_issued');
  const [refundMode, setRefundMode] = useState(order?.refund_mode || '');
  const [refundAmount, setRefundAmount] = useState(order?.refund_amount || order?.total_amount || 0);
  const [refundNotes, setRefundNotes] = useState(order?.refund_notes || '');
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const handleSave = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_BASE_URL}/orders/${order.id}/refund`,
        {
          refund_status: refundStatus,
          refund_mode: refundMode,
          refund_amount: parseFloat(refundAmount),
          refund_notes: refundNotes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Refund details saved');
      onSuccess(response.data.order);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save refund');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncToShopify = async () => {
    if (refundStatus !== 'issued') {
      toast.error('Please mark refund as "Issued" before syncing to Shopify');
      return;
    }

    setSyncing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/orders/${order.id}/refund/sync-shopify`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const shopifyRefund = response.data.shopify_refund;
      if (shopifyRefund?.success) {
        toast.success(`Refund synced to Shopify successfully! Refund ID: ${shopifyRefund.id}`);
      } else if (shopifyRefund?.fallback) {
        toast.warning(`Refund marked in Shopify (manual processing may be required): ${shopifyRefund.message}`);
      } else {
        toast.success('Refund synced to Shopify');
      }
      
      onSuccess(response.data.order || order);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync refund to Shopify');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-wt-paper border-wt-border max-w-md">
        <DialogHeader>
          <DialogTitle className="text-wt-text-primary flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-wt-gold" />
            Refund - Order #{order?.order_number}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Refund Status */}
          <div>
            <Label className="text-wt-text-primary">Refund Status</Label>
            <Select value={refundStatus} onValueChange={setRefundStatus}>
              <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-wt-paper border-wt-border">
                <SelectItem value="not_issued" className="text-wt-text-primary">Not Issued</SelectItem>
                <SelectItem value="issued" className="text-wt-text-primary">Issued</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Refund Mode */}
          <div>
            <Label className="text-wt-text-primary">Refund Mode</Label>
            <Select value={refundMode} onValueChange={setRefundMode}>
              <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary mt-2">
                <SelectValue placeholder="Select mode" />
              </SelectTrigger>
              <SelectContent className="bg-wt-paper border-wt-border">
                <SelectItem value="original_payment" className="text-wt-text-primary">Original Payment Method</SelectItem>
                <SelectItem value="store_credit" className="text-wt-text-primary">Store Credit</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Refund Amount */}
          <div>
            <Label className="text-wt-text-primary">Refund Amount</Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={refundAmount}
              onChange={(e) => setRefundAmount(e.target.value)}
              className="bg-wt-bg border-wt-border text-wt-text-primary mt-2"
            />
            <p className="text-xs text-wt-text-muted mt-1">
              Order Total: {formatCurrency ? formatCurrency(order?.total_amount) : order?.total_amount}
            </p>
          </div>

          {/* Refund Notes */}
          <div>
            <Label className="text-wt-text-primary">Notes</Label>
            <Textarea
              value={refundNotes}
              onChange={(e) => setRefundNotes(e.target.value)}
              placeholder="Refund notes..."
              className="bg-wt-bg border-wt-border text-wt-text-primary mt-2"
              rows={3}
            />
          </div>

          {/* Shopify Sync Status */}
          {order?.source === 'shopify' && (
            <div className="p-3 rounded-lg bg-wt-bg border border-wt-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-wt-text-primary">Shopify Sync</p>
                  <p className="text-xs text-wt-text-muted">
                    {order?.refund_synced_to_shopify ? (
                      <span className="text-green-400">Synced</span>
                    ) : (
                      <span className="text-yellow-400">Not synced</span>
                    )}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleSyncToShopify}
                  disabled={syncing || refundStatus !== 'issued' || order?.refund_synced_to_shopify}
                  className="border-wt-border text-wt-text-primary"
                >
                  {syncing ? 'Syncing...' : 'Sync to Shopify'}
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-wt-border text-wt-text-primary">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading}
            className="bg-wt-gold text-black hover:bg-wt-gold-hover"
          >
            {loading ? 'Saving...' : 'Save Refund'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Audit Log Dialog
export const AuditLogDialog = ({ open, onClose, order }) => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (open && order) {
      loadAuditLog();
    }
  }, [open, order]);

  const loadAuditLog = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/orders/${order.id}/audit-log`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAuditLogs(response.data.audit_logs || []);
    } catch (error) {
      toast.error('Failed to load audit log');
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const getActionIcon = (actionType) => {
    switch (actionType) {
      case 'reopen': return <RotateCcw className="w-4 h-4 text-yellow-400" />;
      case 'return_replacement': return <RefreshCw className="w-4 h-4 text-blue-400" />;
      case 'refund': return <DollarSign className="w-4 h-4 text-green-400" />;
      case 'refund_shopify_sync': return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'status_change': return <Clock className="w-4 h-4 text-purple-400" />;
      case 'driver_assign': return <Package className="w-4 h-4 text-orange-400" />;
      default: return <FileText className="w-4 h-4 text-wt-text-muted" />;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-wt-paper border-wt-border max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-wt-text-primary flex items-center gap-2">
            <History className="w-5 h-5 text-wt-gold" />
            Audit Log - Order #{order?.order_number}
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          {loading ? (
            <div className="text-center py-8 text-wt-text-muted">Loading...</div>
          ) : auditLogs.length === 0 ? (
            <div className="text-center py-8 text-wt-text-muted">No audit log entries</div>
          ) : (
            <div className="space-y-4">
              {auditLogs.map((log) => (
                <div key={log.id} className="p-4 rounded-lg bg-wt-bg border border-wt-border">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5">{getActionIcon(log.action_type)}</div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-wt-text-primary capitalize">
                          {log.action_type.replace(/_/g, ' ')}
                        </span>
                        <span className="text-xs text-wt-text-muted">
                          {formatTimestamp(log.timestamp)}
                        </span>
                      </div>
                      <p className="text-sm text-wt-text-secondary mt-1">
                        By: {log.user_name}
                      </p>
                      {log.previous_status && log.new_status && (
                        <p className="text-sm text-wt-text-muted mt-1">
                          Status: {log.previous_status} → {log.new_status}
                        </p>
                      )}
                      {log.sku_changes && log.sku_changes.length > 0 && (
                        <div className="mt-2 text-sm">
                          <p className="text-wt-text-muted">SKU Changes:</p>
                          {log.sku_changes.map((change, idx) => (
                            <p key={idx} className="text-wt-text-secondary">
                              {change.original_sku} → {change.new_sku} (Qty: {change.quantity})
                            </p>
                          ))}
                        </div>
                      )}
                      {log.notes && (
                        <p className="text-sm text-wt-text-muted mt-2 italic">
                          "{log.notes}"
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-wt-border text-wt-text-primary">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default {
  ReopenOrderDialog,
  ReturnReplacementDialog,
  RefundDialog,
  AuditLogDialog
};
